import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeftIcon,
  CalendarDaysIcon,
  ChevronRightIcon,
  PackageIcon,
} from '@/shared/ui/icons';
import { PageHeader } from '@/shared/ui/layout/PageHeader';
import { Spinner } from '@/shared/ui/feedback/Spinner';
import { EmptyState } from '@/shared/ui/feedback/EmptyState';
import { ErrorState } from '@/shared/ui/feedback/ErrorState';
import { Button } from '@/shared/ui/buttons/Button';
import { extractErrorMessage } from '@/shared/api/errors';
import { useUserPackages } from '@/entities/user-packages';
import { formatDate } from '@/shared/utils/date';
import type { ApiUserPackagesGetItem } from '@/shared/types/api/packages';

// Owner spec: 10 packages per page.
const PAGE_SIZE = 10;

type RowProps = {
  pkg: ApiUserPackagesGetItem;
  onOpen: (id: string) => void;
};

const PackageRow = ({ pkg, onOpen }: RowProps) => {
  const { t } = useTranslation();
  return (
    <li
      className="group flex items-center gap-3 rounded-2xl border border-slate-200 bg-white p-4 shadow-card transition-colors hover:border-brand-200 hover:bg-brand-50/40"
    >
      <span className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-brand-50 text-brand-700">
        <PackageIcon className="h-5 w-5" aria-hidden />
      </span>
      <div className="min-w-0 flex-1">
        <div className="truncate text-sm font-semibold text-ink">{pkg.name}</div>
        <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-ink-muted">
          <span className="inline-flex items-center gap-1">
            <CalendarDaysIcon className="h-3.5 w-3.5" aria-hidden />
            {formatDate(pkg.created_at)}
          </span>
          <span className="inline-flex items-center gap-1">
            <PackageIcon className="h-3.5 w-3.5" aria-hidden />
            {t('packages:parcelsCount', { count: pkg.parcels_count })}
          </span>
        </div>
      </div>
      <Button
        variant="outline"
        size="sm"
        onClick={() => onOpen(pkg.id)}
        rightIcon={<ChevronRightIcon className="h-4 w-4" />}
      >
        {t('packages:batafsil')}
      </Button>
    </li>
  );
};

export const WidgetsPackagesMain = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const query = useUserPackages(page, PAGE_SIZE);

  const totalPages = query.data
    ? Math.max(1, Math.ceil(query.data.count / PAGE_SIZE))
    : 1;
  const items = query.data?.results ?? [];

  return (
    <>
      <PageHeader title={t('packages:title')} />
      <div className="flex flex-col gap-3 p-4 lg:gap-4 lg:p-6">
        {query.isLoading ? (
          <div className="flex justify-center py-12">
            <Spinner size="lg" />
          </div>
        ) : query.error ? (
          <ErrorState
            message={extractErrorMessage(query.error, t('packages:loadError'))}
            onRetry={() => void query.refetch()}
          />
        ) : items.length === 0 ? (
          <EmptyState
            icon={<PackageIcon className="h-10 w-10" />}
            title={t('packages:listEmpty')}
          />
        ) : (
          <>
            <ul className="flex flex-col gap-3">
              {items.map((pkg) => (
                <PackageRow
                  key={pkg.id}
                  pkg={pkg}
                  onOpen={(id) => navigate(`/packages/${id}`)}
                />
              ))}
            </ul>
            {totalPages > 1 && (
              <div className="flex items-center justify-center gap-3 pt-2">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page <= 1}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  leftIcon={<ArrowLeftIcon className="h-4 w-4" />}
                >
                  {t('common:back')}
                </Button>
                <span className="text-sm text-ink-muted">
                  {page} / {totalPages}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page >= totalPages}
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  rightIcon={<ChevronRightIcon className="h-4 w-4" />}
                >
                  {t('common:next')}
                </Button>
              </div>
            )}
          </>
        )}
      </div>
    </>
  );
};
